import config from 'config'
import Bugsnag from '@bugsnag/js'

import debug from 'util/debug'
import storage from 'util/storage'
import uuid from 'util/uuid'
import { throttle } from 'util/functions'
import { workerUrl } from 'util/releaseChannel'
import {
  getAudioNotificationsState,
  getAudioNotificationsStateTs,
  setAudioNotificationsState,
  setMatrixAudioNotificationState,
} from 'util/audio'
import { isWebPushSupported } from 'util/browser'
import { isGranted } from 'util/webPush/permissions'
import { getSubscription } from 'util/webPush/subscriptions'

import {
  selectAccountPreferenceChatEnabled,
  selectIsFeatureEnabled,
} from 'selectors/app/selectAccountPreferences'

// Matrix includes
import MatrixClientPeg from 'matrix-react-sdk/lib/MatrixClientPeg'
import * as Lifecycle from 'matrix-react-sdk/lib/Lifecycle'
import Presence from 'matrix-react-sdk/lib/Presence'
import SettingsStore from 'matrix-react-sdk/lib/settings/SettingsStore'

import { doSubscribe } from 'actions/webPush/doSubscribe'
import { doFetchWidgetById } from 'ducks/widgets/operations'
import { doUpdatePresence, doUpdatePresenceSettings } from './actions/presence'
import GroovePresence from './Presence'
import './IndexedDb.worker'
import { BULK_USER_PRESENCE_CHANGED } from './actionTypes/presence'
import { MATRIX_CLIENT_READY } from './actionTypes/client'

window.mxSettingsStore = SettingsStore

const {
  chatServerUrl: MATRIX_CHAT_SERVER_URL,
  chatIdentityServerUrl: MATRIX_IDENTITY_SERVER_URL,
} = config
let presenceEvents = []
const CLEAR_STORAGE_TIMEOUT_MS = 2000
const CLEAR_STORAGE_HARD_TIMEOUT_MS = 30000
const MATRIX_IDENTITY_RESET_PENDING_KEY = 'matrixIdentityResetPending'

class MatrixEnvironment {
  constructor(storeInstance) {
    this.store = storeInstance
    this.loadingPromise = null
    this.clearStoragePromise = null
    this.clearStorageCompleted = false
    MatrixClientPeg.setIndexedDbWorkerScript(workerUrl('chat-sw.js'))
    MatrixClientPeg.setCreateOptions({
      storeName: `groove-chat`,
      // For some reason the matrix-js-sdk prefixes the normal index db store
      // with matrix-js-sdk but not the crypto store. Adding the prefix here
      // to keep it consistent
      cryptoStoreName: `matrix-js-sdk:groove-chat-crypto`,
    })
    Presence.setPresenceProvider(GroovePresence)
    this.observeMatrixLoad(this.loadMatrix())
  }

  isChatEnabled() {
    const { getState } = this.store
    return selectAccountPreferenceChatEnabled(getState())
  }

  observeMatrixLoad(promise) {
    promise.catch(error => debug('Matrix load failed: %o', error))
    return promise
  }

  loadMatrix() {
    if (!this.isChatEnabled()) return Promise.resolve(null)
    if (this.loadingPromise) return this.loadingPromise
    const { dispatch } = this.store

    const loadingPromise = (async () => {
      await this.setupLoadedSession()
      await this.loadPresenceFromServer()
      await this.subscribe()
      await this.waitForFirstSync()
      await this.subscribePostFirstSync()
      const client = MatrixClientPeg.get()
      this.ensurePusherRegistration()
      this.syncAudioNotificationsState(
        client.getAccountData('g.notifications.audio'),
        true
      )
      // Setup the default matrix configuration settings
      this.presence = Presence.get()
      this.presence.initClient(client, this.onAccountPresence)
      Object.freeze(this)
      dispatch({ type: MATRIX_CLIENT_READY })
      return this
    })()

    const attempt = loadingPromise.catch(error => {
      if (this.loadingPromise === attempt) {
        this.stopMatrixClientAfterFailedLoad()
        this.loadingPromise = null
      }
      throw error
    })
    this.loadingPromise = attempt

    return this.loadingPromise
  }

  isMatrixIdentityGuardEnforced() {
    const { getState } = this.store
    return selectIsFeatureEnabled(getState(), 'matrix_identity_guard_enforce')
  }

  getStoredSessionOwner() {
    return storage.get('mx_user_id', { ignoreNamespace: true })
  }

  getResetPending() {
    return Boolean(storage.get(MATRIX_IDENTITY_RESET_PENDING_KEY))
  }

  setResetPending() {
    storage.set(MATRIX_IDENTITY_RESET_PENDING_KEY, true)
    return this.getResetPending()
  }

  clearResetPending() {
    storage.remove(MATRIX_IDENTITY_RESET_PENDING_KEY)
    return !this.getResetPending()
  }

  getLiveClientOwner() {
    try {
      const client = MatrixClientPeg.get()
      return client && typeof client.getUserId === 'function'
        ? client.getUserId()
        : null
    } catch (e) {
      return null
    }
  }

  buildCurrentUserCredentials() {
    const { chatId: userId, chatAccessToken: accessToken } =
      storage.get('currentUser') || {}
    if (!userId || !accessToken) return null
    return {
      homeserverUrl: MATRIX_CHAT_SERVER_URL,
      identityServerUrl: MATRIX_IDENTITY_SERVER_URL,
      userId,
      accessToken,
      deviceId: uuid(),
    }
  }

  getOwnerShape(owner) {
    if (!owner) return 'missing'
    return typeof owner === 'string' && owner[0] === '@' ? 'mxid' : 'other'
  }

  getMismatchReason(storedOwner, liveOwner, resetPending) {
    if (resetPending) return 'pending_reset'
    if (storedOwner && liveOwner) return 'stored_and_live_owner_mismatch'
    if (storedOwner) return 'stored_owner_mismatch'
    return 'live_client_mismatch'
  }

  notifyMatrixIdentityGuard(
    reason,
    action,
    storedOwner,
    liveOwner,
    currentUserId
  ) {
    Bugsnag.notify(new Error('Matrix identity guard'), event => {
      event.addMetadata('matrixIdentityGuard', {
        reason,
        hadStoredSession: Boolean(storedOwner),
        hadLiveClient: Boolean(liveOwner),
        chatEnabled: Boolean(this.isChatEnabled()),
        action,
        currentOwnerShape: this.getOwnerShape(currentUserId),
        storedOwnerShape: this.getOwnerShape(storedOwner),
        liveOwnerShape: this.getOwnerShape(liveOwner),
      })
    })
  }

  getClearStoragePromise() {
    if (this.clearStorageCompleted) return Promise.resolve()
    if (!this.clearStoragePromise) {
      const clearPromise = Lifecycle.clearStorage()
      clearPromise.then(
        () => {
          this.clearStorageCompleted = true
          if (this.clearStoragePromise === clearPromise)
            this.clearStoragePromise = null
        },
        () => {
          if (this.clearStoragePromise === clearPromise)
            this.clearStoragePromise = null
        }
      )
      this.clearStoragePromise = clearPromise
    }
    return this.clearStoragePromise
  }

  stopMatrixClientAfterFailedLoad() {
    try {
      Lifecycle.stopMatrixClient()
    } catch (e) {
      // Preserve the original startup failure.
    }
  }

  async clearStorageWithSlowTelemetry(storedOwner, liveOwner, currentUserId) {
    let hardTimeoutId
    const clearPromise = this.getClearStoragePromise()
    const hardTimeout = new Promise((resolve, reject) => {
      hardTimeoutId = setTimeout(
        () => reject(new Error('Matrix clearStorage hard timeout')),
        CLEAR_STORAGE_HARD_TIMEOUT_MS
      )
    })
    const slowTimeoutId = setTimeout(() => {
      this.notifyMatrixIdentityGuard(
        'clear_slow',
        'clear_slow',
        storedOwner,
        liveOwner,
        currentUserId
      )
    }, CLEAR_STORAGE_TIMEOUT_MS)
    try {
      await Promise.race([clearPromise, hardTimeout])
    } finally {
      clearTimeout(slowTimeoutId)
      clearTimeout(hardTimeoutId)
    }
  }

  notifyResetFailed(reason, storedOwner, liveOwner, currentUserId) {
    this.notifyMatrixIdentityGuard(
      reason,
      'reset_failed',
      storedOwner,
      liveOwner,
      currentUserId
    )
  }

  async resetMismatchedMatrixSession(
    storedOwner,
    liveOwner,
    credentials,
    resetPending = false
  ) {
    if (!this.setResetPending()) {
      this.notifyResetFailed(
        'marker_write_failed',
        storedOwner,
        liveOwner,
        null
      )
      throw new Error('Matrix identity reset marker write failed')
    }

    const startedUserId = credentials && credentials.userId
    const startedAccessToken = credentials && credentials.accessToken

    this.notifyMatrixIdentityGuard(
      this.getMismatchReason(storedOwner, liveOwner, resetPending),
      'reset_started',
      storedOwner,
      liveOwner,
      startedUserId
    )
    Lifecycle.stopMatrixClient()

    try {
      await this.clearStorageWithSlowTelemetry(
        storedOwner,
        liveOwner,
        startedUserId
      )
    } catch (error) {
      this.notifyResetFailed(
        'clear_failed',
        storedOwner,
        liveOwner,
        startedUserId
      )
      throw error
    }

    const nextCredentials = this.buildCurrentUserCredentials()
    if (
      !nextCredentials ||
      nextCredentials.userId !== startedUserId ||
      nextCredentials.accessToken !== startedAccessToken
    ) {
      this.notifyResetFailed(
        nextCredentials
          ? 'credentials_changed'
          : 'missing_current_user_credentials',
        storedOwner,
        liveOwner,
        nextCredentials && nextCredentials.userId
      )
      throw new Error('Matrix identity reset credentials changed')
    }

    if (!this.clearResetPending()) {
      this.notifyResetFailed(
        'marker_remove_failed',
        storedOwner,
        liveOwner,
        startedUserId
      )
      throw new Error('Matrix identity reset marker remove failed')
    }

    MatrixClientPeg.replaceUsingCreds(nextCredentials)
    await Lifecycle.doSetLoggedIn(nextCredentials, false)
    this.clearStorageCompleted = false
    this.notifyMatrixIdentityGuard(
      'reset_succeeded',
      'reset_succeeded',
      storedOwner,
      liveOwner,
      startedUserId
    )
  }

  async setupLoadedSession() {
    const credentials = this.buildCurrentUserCredentials()
    const currentUserId = credentials && credentials.userId
    const storedOwner = this.getStoredSessionOwner()
    const liveOwner = this.getLiveClientOwner()
    const resetPending = this.getResetPending()
    const hasMatrixIdentity = storedOwner || liveOwner || resetPending

    if (hasMatrixIdentity && !credentials) {
      if (!resetPending && !this.isMatrixIdentityGuardEnforced()) {
        this.notifyMatrixIdentityGuard(
          'missing_current_user_credentials',
          'observe_only',
          storedOwner,
          liveOwner,
          currentUserId
        )
      } else {
        this.notifyMatrixIdentityGuard(
          'missing_current_user_credentials',
          'missing_current_user_credentials',
          storedOwner,
          liveOwner,
          currentUserId
        )
        throw new Error(
          'CRITICAL: Unable to setup a loaded session. Loading for token and registration failed'
        )
      }
    }

    const storedMismatch = storedOwner && storedOwner !== currentUserId
    const liveMismatch = liveOwner && liveOwner !== currentUserId

    if (credentials && (resetPending || storedMismatch || liveMismatch)) {
      if (!resetPending && !this.isMatrixIdentityGuardEnforced()) {
        this.notifyMatrixIdentityGuard(
          this.getMismatchReason(
            storedMismatch && storedOwner,
            liveMismatch && liveOwner
          ),
          'observe_only',
          storedMismatch && storedOwner,
          liveMismatch && liveOwner,
          currentUserId
        )
      } else {
        await this.resetMismatchedMatrixSession(
          storedMismatch && storedOwner,
          liveMismatch && liveOwner,
          credentials,
          resetPending
        )
        return true
      }
    }

    let loadedSession = await Lifecycle.loadSession({
      fragmentQueryParams: {},
      enableGuest: false,
      guestHsUrl: MATRIX_CHAT_SERVER_URL,
      guestIsUrl: MATRIX_IDENTITY_SERVER_URL,
      defaultDeviceDisplayName: 'None',
    })
    if (!loadedSession && credentials) {
      MatrixClientPeg.replaceUsingCreds(credentials)
      await Lifecycle.doSetLoggedIn(credentials, false)
      loadedSession = true
    }
    if (!loadedSession) {
      throw new Error(
        'CRITICAL: Unable to setup a loaded session. Loading for token and registration failed'
      )
    }
    return true
  }

  onAccountPresence = event => {
    const {
      unavailableTimeMs: autoAway,
      offlineTimeMs: autoOffline,
      state,
      autoPresence,
    } = event.getContent()
    const { dispatch } = this.store
    dispatch(doUpdatePresenceSettings({ autoAway, autoOffline, autoPresence }))
    dispatch(doUpdatePresence(state))
  }

  onAccountData = event => {
    if (event.getType() === 'g.notifications.audio') {
      this.syncAudioNotificationsState(event)
    }
  }

  async subscribe() {
    const client = MatrixClientPeg.get()
    client.on('event', event => {
      if (event.getType() === 'm.presence') {
        this.schedulePresenceUpdate(event)
      }
    })
  }

  async subscribePostFirstSync() {
    const client = MatrixClientPeg.get()
    client.on('accountData', this.onAccountData)
    client.on('event', event => {
      const eventType = event.getType()
      if (
        eventType === 'g.integration.bridge.disconnected' ||
        eventType === 'g.integration.facebook.disconnected' // legacy
      ) {
        const { dispatch } = this.store
        const { widget_id: widgetId } = event.getContent()
        // Refetches widget, which will update it's disconnected state.
        dispatch(doFetchWidgetById(widgetId))
      }
    })
  }

  async loadPresenceFromServer() {
    const serverPresence = await MatrixClientPeg.get().getAccountDataFromServer(
      'g.presence'
    )
    const { state = 'online' } = serverPresence || {}
    MatrixClientPeg.updateSyncPresence(state)
  }

  schedulePresenceUpdate(event) {
    presenceEvents.push(event)

    // This function is debounced to only execute after 500ms. This
    // allows gives the buffer time for us to acumolate presence events
    // before syncing them to state
    this.updatePresenceInState()
  }

  updatePresenceInState = throttle(
    () => {
      this.store.dispatch({
        type: BULK_USER_PRESENCE_CHANGED,
        payload: presenceEvents.reduce((memo, evt) => {
          const userId = evt.getSender()
          const presence = evt.getContent().presence
          if (userId && presence) {
            // eslint-disable-next-line no-param-reassign
            memo.push({ userId, presence })
          }
          return memo
        }, []),
      })
      presenceEvents = []
    },
    500,
    { leading: false }
  )

  waitForFirstSync() {
    if (!this.firstSyncPromise) {
      this.firstSyncPromise = new Promise(resolve => {
        const client = MatrixClientPeg.get()
        client.on('sync', function onSync(state) {
          debug('MatrixClient sync state => %s', state)
          if (state === 'PREPARED') {
            resolve()
            client.off('sync', onSync)
          }
        })
      })
    }
    return this.firstSyncPromise
  }

  getSettingsStore() {
    return SettingsStore
  }

  get() {
    const promise = this.loadMatrix().then(() => MatrixClientPeg.get())
    return this.observeMatrixLoad(promise)
  }

  // TODO (jscheel): Consider checkng this intermittently, not just on load
  async ensurePusherRegistration() {
    if (!isWebPushSupported() || !isGranted()) return
    const client = MatrixClientPeg.get()
    const { dispatch } = this.store
    const { pushers } = await client.getPushers()
    const currentPushSubscription = await getSubscription()
    let shouldResubscribe = false

    // NOTE (jscheel): If we somehow do not have a push subscription in the
    // browser, we need to try and set it back up.
    if (!currentPushSubscription || !currentPushSubscription.endpoint) {
      shouldResubscribe = true
    } else {
      // NOTE (jscheel): This is slightly problematic because we can't ensure that
      // the pusher is set up unless chat is loaded, but it's the best we can do
      // right now. We have to ensure that the pusher exists here becuase synapse
      // will delete the pusher if it fails. This can happen when the service worker
      // is uninstalled or deleted, because the browser will deregister the push
      // endpoint. If synapse pushes a notification through sygnal, and sygnal fails
      // to push the notification to the browser's non-existent push endpoint,
      // synapse will consider this a failure and delete the pusher.
      shouldResubscribe = !pushers.find(pusher => {
        const { app_id: appId, kind, data: { endpoint } = {} } = pusher
        return (
          appId === 'com.groovehq.app.web' &&
          kind === 'htttp' &&
          endpoint === currentPushSubscription.endpoint
        )
      })
    }

    if (shouldResubscribe) {
      dispatch(doSubscribe())
    }
  }

  syncAudioNotificationsState(mxEvent, initialSync = false) {
    if (!mxEvent) return

    const matrixEnabled = !!mxEvent.getContent()?.enabled
    const matrixTs = mxEvent.getContent()?.ts
    if (initialSync) {
      const lsEnabled = getAudioNotificationsState()
      const lsTs = getAudioNotificationsStateTs()
      if (matrixEnabled !== lsEnabled) {
        if (matrixTs > lsTs) {
          setAudioNotificationsState(matrixEnabled, matrixTs)
        } else {
          setMatrixAudioNotificationState(lsEnabled, lsTs)
        }
      }
      return
    }

    setAudioNotificationsState(matrixEnabled, matrixTs)
  }

  async logout() {
    Lifecycle.stopMatrixClient()
    await Lifecycle.clearStorage()
  }
}

export default new MatrixEnvironment(app.store)
